import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import dynamic from 'next/dynamic';
import { ValidationError } from 'yup';
import { useDispatch } from 'react-redux';
import { ParamsStepView, onNext as paramsStepOnNext, schema as paramsSchema } from '../steps/params';
import { PassportStepView, onNext as passportStepOnNext, schema as passportSchema } from '../steps/passport';
import { WorkStepView, onNext as workStepNext, schema as workSchema } from '../steps/work';
import { TaskManager } from '../../../helpers/form.new/task.manager';
import { useFormData, useFormStore } from '../../../store/selectors/formStore';
import { OffersModule } from '../../../helpers/dynamic.modules';
import { MainSkeleton } from '../../landing.common/main.skeleton';
import { useSelection } from '../../../store/reducers/selection';
import { useQueryData } from '../hooks/queryData';
import { useSendSize } from '../hooks/sendSize';
import { useProfile } from '../hooks/profile';
import { useAutoFill } from '../hooks/autofill';
import { useLocal } from '../hooks/local';
import { CREDIT_SELECTION_ABC_EXPERIMENT_IDS, FORM_STEPS, PAGE } from '../../../constants';
import { extendFormStore } from '../../../store/reducers/formStore';
import { getStore } from '../../../store/client';
import { FocusedView } from '../focused.view';
import { stepsConfig } from '../focused.view/constants';
import { EventType, useAnalytics } from '../../hooks/analytics';
import { useIsVitrinaProductLanding } from '../../../store/reducers/autoSendProduct';
import { useAddressNormalizer } from '../hooks/addressLoader';
import { useInitUseDeskAnalytics } from '../hooks/initUseDaeskAnalytics';
import { useSessionStorageData } from '../hooks/sessionData';
import { useRouteLoader } from '../hooks/routeLoader';
import { useExperimentGroups } from '../../hooks/experiments';
import { useEsiaCheckUnsigned } from '../hooks/esiaCheckUnsigned';
import { useStepContext, StepContext } from './step.context';
import { AuthSmsContext, useAuthSmsContext } from './auth.context';
import { useEmergencyExitHandler } from './useEmergencyExitHandler';
import yup from 'helpers/validations/validations';

const OffersNew = dynamic(OffersModule, {
    ssr: false,
    loading: () => <MainSkeleton />,
});

interface Props {
    withAutofill?: boolean;
    withLocal?: boolean;
    withProfile?: boolean;
    withQuery?: boolean;
    formData?: FE.FormData;
    ComponentAfterForm?: React.ComponentType;
    withSilentAuth?: boolean;
    onFormPassed?: () => void;
    setShowLanding?: (value: boolean) => void;
}

const oldStepConfig: Array<{
    View: React.ComponentType;
    SheetView: React.ComponentType;
    stepName: string;
    onStepNext?: () => Promise<boolean>;
    schema: () => yup.BaseSchema;
    title: string;
    icon: JSX.Element;
}> = [
    {
        View: ParamsStepView,
        onStepNext: paramsStepOnNext,
        stepName: FORM_STEPS.PARAMS,
        schema: paramsSchema,
        SheetView: () => <></>,
        title: 'Параметры кредита',
        icon: <></>,
    },
    {
        View: PassportStepView,
        onStepNext: passportStepOnNext,
        stepName: FORM_STEPS.PASSPORT,
        schema: passportSchema,
        SheetView: () => <></>,
        title: 'Паспорт',
        icon: <></>,
    },
    {
        View: WorkStepView,
        onStepNext: workStepNext,
        stepName: FORM_STEPS.WORK,
        schema: workSchema,
        SheetView: () => <></>,
        title: 'Место работы',
        icon: <></>,
    },
];

export const View: React.FC<Props> = (props) => {
    const {
        withAutofill = true,
        withLocal = true,
        withProfile = true,
        withQuery = true,
        ComponentAfterForm = OffersNew,
        withSilentAuth,
        onFormPassed,
        setShowLanding,
    } = props;
    const { page } = useSelection();
    const isVitrinaProductLanding = useIsVitrinaProductLanding();
    const [isShowFocusedViewGroup] = useExperimentGroups({
        name: CREDIT_SELECTION_ABC_EXPERIMENT_IDS.NEW_FOCUSED_FORM,
        value: '1',
    });
    const isShowFocusedView = isShowFocusedViewGroup && !isVitrinaProductLanding;

    const { autoRun, oncePassedSteps = [] } = useFormStore();
    const formData = useFormData();
    const [loading, setLoading] = useState(autoRun);
    const [showOffers, setShowOffers] = useState(false);
    const authSmsContext = useAuthSmsContext(withSilentAuth);
    const [initialized, setInitialized] = useState(false);
    const stepContext = useStepContext(isShowFocusedView ? stepsConfig : oldStepConfig);
    const taskManager = useMemo(() => new TaskManager(), []);
    const dispatch = useDispatch();
    const {
        currentStep,
        currentStepConfig: activeStepData,
        areStepsFinished: isOffersStep,
        analyticStepText,
        setStep,
        prevStep,
    } = stepContext;
    const prevFormData = useRef('');
    const { pushEvent } = useAnalytics();

    const stepLabel = useMemo(() => {
        switch (currentStep) {
            case 0:
                return 'Параметры';
            case 1:
                return 'Паспорт';
            case 2:
                return 'Место работы';
            default:
                return 'Не анкета';
        }
    }, [currentStep]);

    useEmergencyExitHandler(currentStep !== 3, () =>
        pushEvent({
            event: EventType.mainEvent,
            eventCategory: 'Подбор кредита',
            eventAction: 'Закрытие анкеты',
            eventLabel: stepLabel,
        }),
    );

    useEffect(() => {
        setInitialized(true);
    }, []);

    useAutoFill(withAutofill);
    useLocal(withLocal);
    useRouteLoader(taskManager);
    useAddressNormalizer(taskManager);
    useProfile(withProfile, currentStep);
    useSessionStorageData(true);
    useSendSize(typeof window === 'object' && [PAGE.WHITELABEL, PAGE.WAYBACK].includes(page));
    useQueryData(withQuery);
    useInitUseDeskAnalytics();
    useEsiaCheckUnsigned();

    const updatePrevFormData = useCallback(() => {
        const { formData } = getStore().getState().formStore;
        prevFormData.current = JSON.stringify(formData);
    }, []);

    const onNext = useCallback(() => {
        if (isOffersStep) {
            return;
        }

        const { onStepNext, stepName } = activeStepData;

        const formDataString = JSON.stringify(formData);

        if (formDataString !== prevFormData.current || !oncePassedSteps.includes(stepName) || autoRun) {
            if (onStepNext) {
                taskManager.register(onStepNext().finally(() => updatePrevFormData()));
            }
            updatePrevFormData();
        }

        if (!oncePassedSteps.includes(stepName)) {
            dispatch(extendFormStore({ oncePassedSteps: oncePassedSteps.concat(stepName) }));
        }

        stepContext.nextStep();
    }, [activeStepData, stepContext, oncePassedSteps, dispatch, isOffersStep, autoRun, formData]);

    useEffect(() => {
        if (!autoRun || !initialized) {
            return;
        }

        if (!isOffersStep) {
            const schema = activeStepData.schema();
            schema
                .validate(formData)
                .then(onNext)
                .catch((e: ValidationError) => {
                    pushEvent({
                        event: EventType.mainEvent,
                        eventAction: 'Невалидная анкета',
                        eventLabel: `${analyticStepText}|${e.path}|Автопроход|${e.value[e.path || ''] || ''}`,
                    });
                    setLoading(false);
                    dispatch(extendFormStore({ autoRun: false }));
                });
        } else {
            dispatch(extendFormStore({ autoRun: false }));
        }
    }, [autoRun, initialized, currentStep, isOffersStep]);

    useEffect(() => {
        /** При прохождении всех шагов дожидаемся выполнение задач */
        if (isOffersStep) {
            setLoading(true);
            Promise.all(taskManager.tasks).then(() => {
                if (onFormPassed) {
                    onFormPassed();
                }

                setShowOffers(true);
                setLoading(false);
            });
        }
        /** onFormPassed не добавлять, может провоцировать лишние ререндеры */
    }, [taskManager, isOffersStep]);

    useEffect(() => {
        if (!isOffersStep && showOffers) {
            setShowOffers(false);
        }
    }, [isOffersStep]);

    const formStepsConfig = useMemo(
        () => ({
            firstStep: {
                label: 'Параметры',
                value: 0,
            },
            lastStep: {
                label: 'Место работы',
                value: stepContext.stepsConfig.length - 1,
            },
        }),
        [stepContext.stepsConfig],
    );

    const onGoBack = useCallback(
        (step?: number) => {
            if (typeof step === 'number') {
                const safeStep = Math.max(
                    Math.min(step, formStepsConfig.lastStep.value),
                    formStepsConfig.firstStep.value,
                );
                const oncePassedSteps = stepContext.stepsConfig.slice(0, safeStep).map((step) => step.stepName);
                dispatch(extendFormStore({ oncePassedSteps }));
                setStep(safeStep);
            } else {
                prevStep();
            }
        },
        [dispatch, prevStep, setStep, formStepsConfig, stepContext.stepsConfig],
    );

    const CurrentComponent = showOffers ? ComponentAfterForm : isShowFocusedView ? FocusedView : activeStepData.View;

    return (
        <AuthSmsContext.Provider value={{ ...authSmsContext }}>
            <StepContext.Provider value={{ ...stepContext, nextStep: onNext, setShowLanding }}>
                {loading ? (
                    <MainSkeleton />
                ) : (
                    <CurrentComponent onGoBack={onGoBack} formStepsConfig={formStepsConfig} />
                )}
            </StepContext.Provider>
        </AuthSmsContext.Provider>
    );
};
